import { useMemo, useState } from "react";
import {
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  ALIGN_OPTIONS,
  BUTTON_STYLES,
  generateButtonHtml,
  isSafeLinkUrl,
} from "./emailHtml";
import { EmailPreviewSurface, FieldLabel } from "./elements";

const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/** Insert → Button. Text + link + alignment + one of two brand styles; nothing more. */
const ButtonInsert = ({ onInsert }) => {
  const [text, setText] = useState("");
  const [href, setHref] = useState("");
  const [align, setAlign] = useState("center");
  const [style, setStyle] = useState("primary");

  const linkOk = isSafeLinkUrl(href);
  const canInsert = text.trim().length > 0 && linkOk;

  const html = useMemo(
    () => generateButtonHtml({ text, href: linkOk ? href : "#", align, style }),
    [text, href, linkOk, align, style]
  );

  return (
    <Stack spacing={2.5}>
      <TextField
        label="Button text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Get the app"
        fullWidth
        autoFocus
        inputProps={{ maxLength: 80, "data-testid": "button-insert-text" }}
      />
      <TextField
        label="URL"
        value={href}
        onChange={(e) => setHref(e.target.value)}
        placeholder="https://apps.apple.com/…"
        fullWidth
        error={href.trim().length > 0 && !linkOk}
        helperText={
          href.trim().length > 0 && !linkOk ? "Must start with https:// or http://" : " "
        }
        inputProps={{ inputMode: "url", "data-testid": "button-insert-url" }}
      />

      <div>
        <FieldLabel>Alignment</FieldLabel>
        <ToggleButtonGroup
          value={align}
          exclusive
          size="small"
          onChange={(_, v) => v && setAlign(v)}
          fullWidth
        >
          {ALIGN_OPTIONS.map((a) => (
            <ToggleButton key={a} value={a} data-testid={`button-align-${a}`}>
              {capitalise(a)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>

      <div>
        <FieldLabel>Style</FieldLabel>
        <ToggleButtonGroup
          value={style}
          exclusive
          size="small"
          onChange={(_, v) => v && setStyle(v)}
          fullWidth
        >
          {Object.entries(BUTTON_STYLES).map(([id, def]) => (
            <ToggleButton key={id} value={id}>
              {def.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>

      <div>
        <FieldLabel>Preview</FieldLabel>
        {/* Safe: the markup is generated from escaped inputs, never from raw user HTML. */}
        <EmailPreviewSurface dangerouslySetInnerHTML={{ __html: html }} />
      </div>

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button
          variant="contained"
          onClick={() => onInsert(html)}
          disabled={!canInsert}
          data-testid="button-insert-submit"
        >
          Insert into email
        </Button>
      </Stack>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Inserted as plain table/link HTML so it renders in Outlook and Gmail. You can edit
        the text, link or colours afterwards in the body.
      </Typography>
    </Stack>
  );
};

export default ButtonInsert;
